import { Button, Grid, Link, StudioIcon } from '@rbx/ui';
import React, { useMemo } from 'react';
import useAccessibilityStyles from '@modules/miscellaneous/common/styles/accessibilityStyles';
import StudioLogo from '@modules/useStudio/components/StudioLogo';

export type UseStudioVariant = 'button' | 'iconButton' | 'blueLogoIconButton' | 'link';

const defaultButtonProps = {
  color: 'primaryBrand',
  size: 'large',
  variant: 'contained',
  fullWidth: false,
};

const defaultButtonStyle = {
  marginTop: 8,
  marginBottom: 24,
};

const defaultIconProps = {
  fontSize: 'medium',
  color: 'inherit',
} as React.ComponentProps<typeof StudioIcon>;

const defaultIconStyle = {
  marginRight: 8,
};

const UseStudioCTA = ({
  variant,
  text,
  buttonProps,
  buttonStyle = defaultButtonStyle,
  iconProps,
  iconStyle = defaultIconStyle,
  onClick,
  children,
  ...otherCTAProps
}: {
  variant: UseStudioVariant;
  text: string;
  buttonProps?: React.ComponentProps<typeof Button>;
  iconProps?: React.ComponentProps<typeof StudioIcon>;
  buttonStyle?: Record<string, string | number>;
  iconStyle?: Record<string, string | number>;
  onClick: () => void;
  children?: React.ReactNode;
} & (React.ComponentProps<typeof Link> | React.ComponentProps<typeof Button>)) => {
  const {
    classes: { clickable },
  } = useAccessibilityStyles();
  const ctaText = useMemo(() => children ?? text, [text, children]);

  if (variant === 'link')
    return (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <Link
        className={clickable}
        role='button'
        {...otherCTAProps}
        onClick={onClick}
        style={{
          display: 'inline',
          ...buttonStyle,
        }}>
        {ctaText}
      </Link>
    );

  const btnProps = {
    ...defaultButtonProps,
    ...(buttonProps ?? {}),
  } as React.ComponentProps<typeof Button>;

  const studioIconProps = {
    ...defaultIconProps,
    ...(iconProps ?? {}),
  } as React.ComponentProps<typeof StudioIcon>;

  return (
    <Button
      {...btnProps}
      {...otherCTAProps}
      style={{
        ...(buttonStyle ?? {}),
        alignSelf:
          (buttonStyle.alignSelf ?? btnProps.fullWidth)
            ? 'initial'
            : // makes button width of its contents
              'flex-start',
      }}
      onClick={onClick}>
      <Grid container item justifyContent='center' alignItems='center'>
        {variant === 'blueLogoIconButton' && (
          // NOTE (tchu, 2025-09-09): Use dark mode (white logo) for the Studio
          // logo in the CTA button to match the text of the button
          // (white text on blue background).
          <StudioLogo size={26} style={iconStyle} themeMode='dark' />
        )}
        {variant === 'iconButton' && <StudioIcon {...studioIconProps} style={iconStyle} />}
        {ctaText}
      </Grid>
    </Button>
  );
};

export default UseStudioCTA;
